import { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import type { Row } from "@/components/Table";
import Button from "@/components/Button";
import ErrorBoundary from "@/components/ErrorBoundary";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { COMMITTEE_PARTICIPANTS_QUERY } from "../queries";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { canViewCommitteeParticipant } from "./TableColumns";
import type {
  CommitteeParticipantWithCommittee,
  CommitteeParticipantsQueryResponse,
  CommitteeParticipantsQueryVariables
} from "../types";

const COMMITTEE_PARTICIPANTS_MOBILE_LAYOUT =
  "[&_.table-mobile_.flex-1]:!px-0 [&_.table-mobile_.flex.w-full>.flex-1.flex.gap-2]:!px-8";

interface CommitteeParticipantsPanelProps {
  committeeId: string;
  committeeName: string;
  termId?: string;
  participantCount: number;
  columns: ColumnDef<CommitteeParticipantWithCommittee, unknown>[];
  onViewParticipant: (participant: CommitteeParticipantWithCommittee) => void;
}

export default function CommitteeParticipantsPanel({
  committeeId,
  committeeName,
  termId,
  participantCount,
  columns,
  onViewParticipant
}: CommitteeParticipantsPanelProps) {
  const { isCompactLayout } = useIsCompactLayout();
  const [sorting, setSorting] = useState<SortingState>([{ id: "endDate", desc: true }]);
  const panelTitle = termId ? `Participants (${participantCount})` : `Participations (${participantCount})`;
  const loadErrorTitle = termId ? "Unable to load participants" : "Unable to load participations";
  const [fetchParticipants, { data, loading, called, error, refetch }] = useLazyQuery<
    CommitteeParticipantsQueryResponse,
    CommitteeParticipantsQueryVariables
  >(COMMITTEE_PARTICIPANTS_QUERY, {
    fetchPolicy: "cache-and-network",
    variables: {
      input: {
        committeeId,
        ...(termId && { termId })
      }
    }
  });

  useEffect(() => {
    fetchParticipants();
  }, [committeeId, fetchParticipants, participantCount, termId]);

  const participants = useMemo(
    (): CommitteeParticipantWithCommittee[] =>
      (data?.committeeParticipants ?? []).map((participant) => ({
        ...participant,
        committeeName
      })),
    [committeeName, data?.committeeParticipants]
  );

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((currentSorting) =>
      typeof updaterOrValue === "function" ? updaterOrValue(currentSorting) : updaterOrValue
    );
  };

  return (
    <div className={COMMITTEE_PARTICIPANTS_MOBILE_LAYOUT}>
      {error && called && !loading ? (
        <>
          {isCompactLayout && <h3 className="text-title-small px-4 py-4">{panelTitle}</h3>}
          <div className="border-t border-sys-brand-surface-container-highest bg-sys-brand-surface-container px-4 py-8">
            <ErrorBoundary
              variant="inline"
              title={loadErrorTitle}
              message={error.message}
              action={
                <Button variant="outlined" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            />
          </div>
        </>
      ) : (
        <TableDetailsPanel
          data={participants}
          columns={columns}
          title={panelTitle}
          loading={loading || !called}
          sortingState={sorting}
          onSortingChange={handleSortingChange}
          onClickRow={(row: Row<CommitteeParticipantWithCommittee>) => {
            if (canViewCommitteeParticipant(row.original)) {
              onViewParticipant(row.original);
            }
          }}
          getRowClasses={(row) => (canViewCommitteeParticipant(row.original) ? "" : "cursor-default")}
        />
      )}
    </div>
  );
}
